import useFileSaver from "@hooks/useFileSaver";
import useFormatter from "@hooks/useFormatter";
import useRequest, { IDataRequest, IJustificativaRequest } from "@hooks/useRequest";
import { ICliente } from "@model/Cliente";
import { EnumOrigemCancelamento, EnumTipoCobrancaChip, IContratoCliente } from "@model/ContratoCliente";
import IContratoClienteStatus, { EnumContratoClienteStatus } from "@model/ContratoClienteStatus";
import { EnumEmpresa } from "@model/Empresa";
import ILinha from "@model/Linha";
import { EnumOperadora } from "@model/Operadora";
import { IEnum, TDate } from "@utils/TypeHelpers";
import React from "react";

export interface IRegistrarContratoRequest {
    linha: Partial<ILinha>
    cliente: Partial<ICliente>
    contrato: Partial<IContratoCliente>
}

export interface IValidarNumeroResponse {
    linha: ILinha | null,
    contratoCliente: IContratoCliente | null
}

export interface IValidarDocumentoResponse {
    cliente: ICliente | null
    possuiDebitos: number
}

export interface IContratoClienteFilter {
    contratoClienteId?: number
    clienteNome?: string
    clienteCpf?: string
    linhaNumero?: string
    linhaContratoOperadoraEmpresaId?: EnumEmpresa
    linhaContratoOperadoraOperadoraId?: EnumOperadora
    contratoClienteStatusId?: EnumContratoClienteStatus
    usuarioLinha?: string
    whatsappEnvio?: string
}

export interface ICancelarContratoRequest extends IJustificativaRequest {
    vencimentoFatura: TDate
    valorFatura: number
    origemCancelamento: EnumOrigemCancelamento
}

const controller = "ContratoCliente";

export function useContratoClienteService () {
    const _request = useRequest();
    const fileSaver = useFileSaver();
    const formatter = useFormatter();

    const listar = React.useCallback((request: IDataRequest<IContratoClienteFilter>) => {
        return _request.getData<IContratoCliente[]>(`${controller}/Listar`, request);
    }, [_request]);

    const carregar = React.useCallback((contratoClienteId: number) => {
        return _request.get<IContratoCliente>(`${controller}/Carregar/${contratoClienteId}`);
    }, [_request]);

    const listarStatusAtivos = React.useCallback(() => {
        return _request.get<IContratoClienteStatus[]>(`${controller}/ListarStatusAtivos`);
    }, [_request]);

    const listarOrigens = React.useCallback(() => {
        return _request.get<IEnum<EnumOrigemCancelamento>[]>(`${controller}/ListarOrigens`);
    }, [_request]);

    const listarTipoCobrancaChip = React.useCallback(() => {
        return _request.get<IEnum<EnumTipoCobrancaChip>[]>(`${controller}/ListarTiposCobrancaChip`);
    }, [_request]);

    const registrar = React.useCallback((request: IRegistrarContratoRequest) => {
        return _request.post<IContratoCliente>(`${controller}/Registrar`, request);
    }, [_request]);

    const cancelar = React.useCallback((contratoClienteId: number, request: ICancelarContratoRequest) => {
        return _request.post<IContratoCliente>(`${controller}/Cancelar/${contratoClienteId}`, request);
    }, [_request]);

    const reativar = React.useCallback((contratoClienteId: number, justificativaRequest: IJustificativaRequest) => {
        return _request.post<IContratoCliente>(`${controller}/Reativar/${contratoClienteId}`, justificativaRequest);
    }, [_request]);

    const editar = React.useCallback((contratoClienteId: number, contratoCliente: IContratoCliente) => {
        return _request.post<IContratoCliente>(`${controller}/Editar/${contratoClienteId}`, contratoCliente);
    }, [_request]);

    const cancelarParaRegistrarOutro = React.useCallback((contratoClienteId: number) => {
        return _request.get<ILinha>(`${controller}/CancelarParaRegistrarOutro/${contratoClienteId}`);
    }, [_request]);

    const validarNumero = React.useCallback((numero: string) => {
        return _request.get<IValidarNumeroResponse>(`${controller}/ValidarNumero/${numero}`);
    }, [_request]);

    const validarDocumento = React.useCallback((documento: string) => {
        return _request.get<IValidarDocumentoResponse>(`${controller}/ValidarDocumento/${formatter.extractNumbers(documento)}`);
    }, [_request, formatter]);

    const gerarSegundaVia = React.useCallback((contratoClienteId: number) => {
        return _request.download(`/${controller}/GerarSegundaVia/${contratoClienteId}`)
            .then(response => { 
                fileSaver.save(response, `Contrato-${contratoClienteId}.xml`);
            });
    }, [_request, fileSaver]);

    const obterValorFaturaCancelamento = React.useCallback((contratoClienteId: number) => {
        return _request.get<number>(`${controller}/ObterValorFaturaCancelamento/${contratoClienteId}`);
    }, [_request]);


    return React.useMemo(() => ({
        listar, carregar, gerarSegundaVia, registrar, listarStatusAtivos, listarOrigens, listarTipoCobrancaChip, cancelarParaRegistrarOutro, cancelar, reativar, editar, validarNumero, validarDocumento, obterValorFaturaCancelamento
    }), [listar, carregar, gerarSegundaVia, registrar, listarStatusAtivos, listarOrigens, listarTipoCobrancaChip, cancelarParaRegistrarOutro, cancelar, reativar, editar, validarNumero, validarDocumento, obterValorFaturaCancelamento]);
}